import { Country } from '@afterpay/types'
import { AddressMode, nonShippingInfoAddressModes } from '../../model'
import type { TermsContext } from './termsContext'

const isStandardOrBuyNowExpress = (isBuyNow: boolean, isExpress: boolean): boolean => {
  const isBuyNowExpress = isExpress && isBuyNow
  return !isExpress || isBuyNowExpress
}

const isDeferredShippingTerms = (isDeferredShipping: boolean, isBuyNow: boolean, addressMode: AddressMode | undefined, isExpress: boolean): boolean => {
  const deferringShippingAddressMode = isDeferredShipping && !nonShippingInfoAddressModes.includes(addressMode)
  const isExpressWithoutBuyNow = !isBuyNow && isExpress
  return deferringShippingAddressMode || isExpressWithoutBuyNow
}

const isUkRegulatedDeferredShippingTerms = (isDeferredShipping: boolean, isBuyNow: boolean, isExpress: boolean): boolean => {
  const isExpressWithoutBuyNow = !isBuyNow && isExpress
  return isDeferredShipping || isExpressWithoutBuyNow
}

const isExpressNotDeferred = (isDeferredShipping: boolean, isExpress: boolean): boolean => isExpress && !isDeferredShipping

const partialLateFeeKey = (initialCopy: string, preferredDayShown: boolean): string => `${initialCopy}${preferredDayShown ? '_preferredDayShown' : ''}`

const isUSStandardOrBuyNowExpress = (c: TermsContext): boolean => c.country === Country.US && isStandardOrBuyNowExpress(c.isBuyNow, c.isExpress)

const isDeferred = (c: TermsContext): boolean => isDeferredShippingTerms(c.isDeferredShipping, c.isBuyNow, c.addressMode, c.isExpress)

const shouldShowFailedPaymentFeeTerms = (c: TermsContext): boolean => c.failedPaymentFeeApplies && !c.isSupFlow && !c.isGiftCardFlow && c.isAutopayOn

const failedPaymentFeeKey = (key: string, c: TermsContext): string => (shouldShowFailedPaymentFeeTerms(c) ? `${key}_failedPaymentFee` : key)

type Rule = { when: (c: TermsContext) => boolean; key: (c: TermsContext) => string }

const RULES: Rule[] = [
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.isBillingAgreementApprovalProcess && !c.isZeroLateFee && c.shouldCreditCheck,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.lateFee_billingAgreementApprovalProcessWithCreditCheckConsent', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.isBillingAgreementApprovalProcess && !c.isZeroLateFee,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.lateFee_billingAgreementApprovalProcess', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.isBillingAgreementApprovalProcess && c.isZeroLateFee && c.shouldCreditCheck,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.noLateFee_billingAgreementApprovalProcessWithCreditCheckConsent', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.isBillingAgreementApprovalProcess && c.isZeroLateFee,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.noLateFee_billingAgreementApprovalProcess', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasBillingAgreements && c.isZeroLateFee && c.shouldCreditCheck,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.noLateFee_billingAgreementWithCreditCheckConsent', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasBillingAgreements && c.isZeroLateFee,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.noLateFee_billingAgreement', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasBillingAgreements && !c.isZeroLateFee && c.shouldCreditCheck,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.lateFee_billingAgreementWithCreditCheckConsent', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasBillingAgreements && !c.isZeroLateFee,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.lateFee_billingAgreement', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasEstimatedPPA && c.isZeroLateFee && c.shouldCreditCheck,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.noLateFee_creditCheckConsent', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasEstimatedPPA && c.isZeroLateFee,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.noLateFee', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && !c.hasEstimatedPPA && c.isZeroLateFee && c.shouldCreditCheck,
    key: (c) => failedPaymentFeeKey('summary:terms.noEstimatedPPA.noLateFee_creditCheckConsent', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && !c.hasEstimatedPPA && c.isZeroLateFee,
    key: (c) => failedPaymentFeeKey('summary:terms.noEstimatedPPA.noLateFee', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasEstimatedPPA && !c.isZeroLateFee && c.shouldCreditCheck,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.lateFee_creditCheckConsent', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && c.hasEstimatedPPA && !c.isZeroLateFee,
    key: (c) => failedPaymentFeeKey('summary:terms.estimatedPPA.lateFee', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && !c.hasEstimatedPPA && !c.isZeroLateFee && c.shouldCreditCheck,
    key: (c) => failedPaymentFeeKey('summary:terms.noEstimatedPPA.lateFee_creditCheckConsent', c),
  },
  {
    when: (c) => isUSStandardOrBuyNowExpress(c) && !c.hasEstimatedPPA && !c.isZeroLateFee,
    key: (c) => failedPaymentFeeKey('summary:terms.noEstimatedPPA.lateFee', c),
  },

  {
    when: (c) => c.country === Country.US && isDeferred(c) && !c.isZeroLateFee && c.shouldCreditCheck,
    key: () => 'summary:terms.lateFee_deferredShippingWithCreditCheckConsent',
  },
  {
    when: (c) => c.country === Country.US && isDeferred(c) && !c.isZeroLateFee,
    key: () => 'summary:terms.lateFee_deferredShipping',
  },

  {
    when: (c) => c.hasBillingAgreements && c.showTermsDisclosure,
    key: () => 'summary:terms_billingAgreement_termsNotAccepted',
  },
  {
    when: (c) => c.hasBillingAgreements && c.country === Country.NZ,
    key: () => 'summary:terms_billingAgreement_updated',
  },
  {
    when: (c) => c.hasBillingAgreements && c.hasPartialLateFeesEnabled,
    key: (c) => partialLateFeeKey('summary:terms_billingAgreementPartialLateFees', c.preferredDayShown),
  },
  {
    when: (c) => c.hasBillingAgreements,
    key: () => 'summary:terms_billingAgreement',
  },

  {
    when: (c) => c.country === Country.GB && isUkRegulatedDeferredShippingTerms(c.isDeferredShipping, c.isBuyNow, c.isExpress),
    key: () => 'summary:termsFCA_regulated',
  },

  {
    when: (c) => isDeferred(c) && c.shouldCreditCheck,
    key: () => 'summary:terms_deferredShippingWithCreditCheckConsent',
  },
  {
    when: (c) => isDeferred(c) && c.showTermsDisclosure,
    key: () => 'summary:terms_deferredShipping_termsNotAccepted',
  },
  {
    when: (c) => isDeferred(c) && c.country === Country.NZ,
    key: () => 'summary:terms_deferredShipping_updated',
  },
  {
    when: (c) => isDeferred(c) && c.hasPartialLateFeesEnabled,
    key: (c) => partialLateFeeKey('summary:terms_deferredShippingPartialLateFees', c.preferredDayShown),
  },
  {
    when: isDeferred,
    key: () => 'summary:terms_deferredShipping',
  },

  {
    when: (c) => c.country === Country.NZ && c.showTermsDisclosure,
    key: () => 'summary:terms_termsNotAccepted',
  },
  {
    when: (c) =>
      c.country === Country.NZ && c.hasCreditCheckConsent === false && !c.preferredDayShown && !isExpressNotDeferred(c.isDeferredShipping, c.isExpress),
    key: () => 'summary:terms_creditCheckConsent',
  },
  {
    when: (c) => c.country === Country.NZ,
    key: (c) => (c.preferredDayShown ? 'summary:terms_preferredDayShown' : 'summary:terms_updated'),
  },

  {
    when: (c) => c.country === Country.GB,
    key: () => 'summary:termsFCA_regulated',
  },

  {
    when: (c) => c.hasPartialLateFeesEnabled,
    key: (c) => partialLateFeeKey('summary:terms_partialLateFees', c.preferredDayShown),
  },

  { when: () => true, key: () => 'summary:terms' },
]

export const selectTermsKey = (c: TermsContext): string => {
  for (const rule of RULES) {
    if (rule.when(c)) return rule.key(c)
  }
  return 'summary:terms'
}
